import { useState, useEffect } from "react";
import { useAuth } from '../../../context/AuthProvider';
import { normalizeMode } from '../../../utils/market';
import { Loader2, Save, CreditCard } from "lucide-react";
import { TabSkeleton } from "../BotManagement/BotManagementSkeleton";
import { useParams } from "react-router-dom";
import api, { authService } from "../../../services/api";
import { useNotification } from "../../../components/Notifications/NotificationContext";
import { useSyncBotSelection } from "../../../hooks/useSyncBotSelection";
import { validateFields } from "../../../utils/contentBlacklistValidator";
import PlanManager from "../../../components/PlanManager/PlanManager";
import DefaultDeliveryCard from "./DefaultDeliveryCard";
import { useGatewayList } from "../../../hooks/useOnlyUpsellBlocked";
import { isOnlyUpsellBlocked } from "../../../utils/gateways";
import "./index.css";

export default function Plans() {
    const { botId } = useParams();
    const { showError, showSuccess } = useNotification();

    const [plans, setPlans] = useState([]);
    // Snapshot no formato de arrays paralelas que o PlanManager consome.
    // Só é redefinido ao (re)carregar do servidor — nunca com o output do próprio
    // componente, senão o useEffect interno resetaria o estado a cada digitação.
    const [initialPlans, setInitialPlans] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingPage, setLoadingPage] = useState(true);
    const [currencyMode, setCurrencyMode] = useState('brl'); // 'brl' = R$ | 'usd' = $ | 'mxn' = LATAM
    const [baseCurrency, setBaseCurrency] = useState(null);  // LATAM: MXN, USD, COP ou CLP
    const gateways = useGatewayList();
    // "Apenas Upsell" indisponível em bot Global/com cartão (backend recusa salvar).
    const isAdmin = useAuth()?.user?.is_admin === true;
    const onlyUpsellBlocked = isOnlyUpsellBlocked({ currencyMode, gateways: gateways || [], botIds: [botId], isAdmin });
    // Entrega padrão (fallback) do bot. null = grupo VIP.
    const [defaultDelivery, setDefaultDelivery] = useState(null);

    useSyncBotSelection();

    // Carrega dados do bot ao inicializar
    useEffect(() => {
        if (!botId) return;
        loadPlansData();
    }, [botId]);

    const loadPlansData = async () => {
        try {
            setLoadingPage(true);
            const { data } = await authService.bots.edit_bot(botId);
            const bot = data.bot;
            setCurrencyMode(normalizeMode(bot.pix_config?.currency_mode));
            setBaseCurrency(bot.pix_config?.latam_base_currency || null);
            setDefaultDelivery(bot.default_delivery || null);

            // Monta planos a partir das arrays separadas
            const planNames = Array.isArray(bot.plan_names) ? bot.plan_names : [];
            const pixValues = Array.isArray(bot.pix_values) ? bot.pix_values : [];
            const planDurations = Array.isArray(bot.plan_duration) ? bot.plan_duration : [];
            const planColors = Array.isArray(bot.plan_color) ? bot.plan_color : [];
            // Entrega por plano (alinhada por índice a partir dos objetos completos).
            const planObjs = Array.isArray(bot.plans) ? bot.plans : [];
            const planDeliveries = planNames.map((_, idx) => planObjs[idx]?.delivery || null);

            const plans = planNames.map((name, idx) => ({
                name,
                value: pixValues[idx] || '',
                duration: planDurations[idx] || 'mensal',
                color: planColors[idx] || 'default',
                delivery: planDeliveries[idx] || null
            }));

            setPlans(plans);
            // Alimenta o PlanManager (arrays paralelas). Novo objeto = re-hidrata.
            setInitialPlans({
                names: planNames,
                values: pixValues,
                durations: planDurations,
                color: planColors,
                deliveries: planDeliveries,
            });
        } catch (err) {
            console.error(err);
            showError("Erro ao carregar planos");
        } finally {
            setLoadingPage(false);
        }
    };

    // Recebe as arrays paralelas do PlanManager e converte de volta para o
    // formato de objetos usado no submit.
    const handlePlansUpdate = (pm) => {
        const next = (pm.names || []).map((name, idx) => ({
            name,
            value: pm.values?.[idx] ?? '',
            duration: pm.durations?.[idx] || 'mensal',
            color: pm.color?.[idx] || 'default',
            delivery: pm.deliveries?.[idx] || null,
        }));
        setPlans(next);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Validação básica
            const validPlans = plans
                .filter(p => p.name && p.value)
                .map((plan) => ({
                    ...plan,
                    color: plan.color || 'default'
                }));
            if (validPlans.length === 0) {
                showError("Adicione pelo menos um plano válido");
                setLoading(false);
                return;
            }

            const contentCheck = validateFields(
                validPlans.map((plan, index) => ({
                    label: `Plano ${index + 1} - Nome`,
                    value: plan.name,
                }))
            );
            if (!contentCheck.isValid) {
                showError(contentCheck.error);
                setLoading(false);
                return;
            }

            // Enviar dados para a rota dedicada de planos
            const botData = {
                plans: validPlans,
                default_delivery: defaultDelivery,
            };

            const { data: response } = await api.put(`/api/bots/edit/plans/${botId}`, botData);

            if (!response.success) {
                throw new Error(response.error || "Erro ao atualizar planos");
            }

            showSuccess("Planos atualizado com sucesso!");
            loadPlansData();
        } catch (err) {
            console.error(err);
            const errorMsg = err?.response?.data?.error || err?.message || "Erro ao salvar planos";
            showError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {loadingPage ? (
                <TabSkeleton tab="plans" />
            ) : (
                <form className="spt-reveal" onSubmit={handleSubmit} style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
                    <div className="plans-layout">
                        {/* ESQUERDA — planos */}
                        <div className="plans-main panel-card text-white w-full">
                            <div className="p-4 md:p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <CreditCard size={20} className="text-[#a78bfa]" />
                                        <div>
                                            <h3 className="font-semibold text-white">Planos de Pagamento</h3>
                                            <p className="text-xs text-white/40">Gerencie os planos disponíveis para seus clientes</p>
                                        </div>
                                    </div>
                                    <span className="text-sm text-white/30">
                                        {plans.length} {plans.length === 1 ? 'plano' : 'planos'}
                                    </span>
                                </div>

                                <PlanManager
                                    initialPlans={initialPlans}
                                    onUpdate={handlePlansUpdate}
                                    label=""
                                    currencyMode={currencyMode}
                                    baseCurrency={baseCurrency}
                                    enableDelivery={true}
                                    onlyUpsellBlocked={onlyUpsellBlocked}
                                />
                            </div>
                        </div>

                        {/* DIREITA — entrega padrão (fallback) */}
                        <aside className="plans-side">
                            <DefaultDeliveryCard
                                value={defaultDelivery}
                                onChange={setDefaultDelivery}
                                onlyUpsellBlocked={onlyUpsellBlocked}
                            />

                            <button
                                type="submit"
                                disabled={loading || plans.length === 0}
                                className="btn-primary w-full block"
                            >
                                {loading ? (
                                    <><Loader2 size={14} className="animate-spin me-2 inline" /> Salvando...</>
                                ) : (
                                    <><Save size={14} className="me-2 inline" /> Atualizar Planos</>
                                )}
                            </button>
                        </aside>
                    </div>
                </form>
            )}
        </>
    );
}